import type { ReactNode } from 'react';
import { cn } from './cn';

/**
 * Table primitives.
 *
 * A dense table is the right shape for this platform (§2: information-dense
 * without feeling cluttered), so the horizontal scroll is contained here rather
 * than being allowed to escape and scroll the whole page on a narrow screen.
 */
export function TableWrap({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('scrollbar-thin -mx-px overflow-x-auto', className)}>
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  );
}

export function Th({
  children,
  align = 'left',
  className,
  scope = 'col',
}: {
  children?: ReactNode;
  align?: 'left' | 'right' | 'center';
  className?: string;
  scope?: 'col' | 'row';
}) {
  return (
    <th
      scope={scope}
      className={cn(
        'border-b border-border bg-surface-sunken px-3 py-2 text-[11px] font-semibold tracking-wide',
        'text-text-subtle uppercase whitespace-nowrap',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        align === 'left' && 'text-left',
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  align = 'left',
  className,
  colSpan,
  title,
}: {
  children?: ReactNode;
  align?: 'left' | 'right' | 'center';
  className?: string;
  colSpan?: number;
  /** Exact value on hover, where the cell shows a relative or rounded one. */
  title?: string;
}) {
  return (
    <td
      colSpan={colSpan}
      title={title}
      className={cn(
        'border-b border-border px-3 py-2.5 align-middle text-text',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        className,
      )}
    >
      {children}
    </td>
  );
}

export function Tr({
  children,
  onClick,
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <tr
      onClick={onClick}
      className={cn(
        'transition-colors hover:bg-surface-hover',
        onClick && 'cursor-pointer',
        className,
      )}
    >
      {children}
    </tr>
  );
}

/** A sortable column header that shows and toggles its direction. */
export function SortableTh<T extends string>({
  children,
  sortKey,
  active,
  onSort,
  align = 'left',
}: {
  children: ReactNode;
  sortKey: T;
  active: boolean;
  onSort: (key: T) => void;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <Th align={align} className="p-0">
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        aria-sort={active ? 'ascending' : 'none'}
        className={cn(
          'flex w-full items-center gap-1 px-3 py-2 text-[11px] font-semibold tracking-wide uppercase',
          'transition-colors hover:text-text',
          active ? 'text-accent-text' : 'text-text-subtle',
          align === 'right' && 'justify-end',
          align === 'center' && 'justify-center',
        )}
      >
        {children}
      </button>
    </Th>
  );
}
